import type { Message } from '../types';

const VISITOR_KEY = 'grippy:visitor';
const CONVERSATION_KEY = 'grippy:conversation';
const MESSAGES_KEY = 'grippy:messages';
const LAST_ACTIVITY_KEY = 'grippy:last-activity';

export function getVisitorId(): string {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = generateUuid();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

export function getConversationUuid(): string | null {
  return localStorage.getItem(CONVERSATION_KEY);
}

export function setConversationUuid(uuid: string): void {
  localStorage.setItem(CONVERSATION_KEY, uuid);
}

/** Clear only the conversation_uuid — keeps messages around so the self-heal
 *  flow (stale uuid → new conversation) doesn't erase the visible thread. */
export function clearConversation(): void {
  localStorage.removeItem(CONVERSATION_KEY);
}

/** Full reset — used when a chat ends (inactivity timeout, manual close). */
export function clearAll(): void {
  localStorage.removeItem(CONVERSATION_KEY);
  localStorage.removeItem(MESSAGES_KEY);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
}

/** Persist the running message list across page navigations + panel reopens. */
export function saveMessages(messages: Message[]): void {
  try {
    // Cap stored history to the last 50 messages so localStorage doesn't bloat.
    const trimmed = messages.slice(-50);
    localStorage.setItem(MESSAGES_KEY, JSON.stringify(trimmed));
  } catch {
    /* quota exceeded — silently drop */
  }
}

export function loadMessages(): Message[] {
  try {
    const raw = localStorage.getItem(MESSAGES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (m): m is Message =>
        m && typeof m.id === 'string' && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string',
    );
  } catch {
    return [];
  }
}

export function touchActivity(): void {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
}

export function lastActivityAt(): number {
  const raw = localStorage.getItem(LAST_ACTIVITY_KEY);
  const n = raw ? parseInt(raw, 10) : 0;
  return Number.isFinite(n) ? n : 0;
}

function generateUuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback (older browsers)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
